import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiStar, FiUsers, FiHelpCircle } = FiIcons;

const NicheDefinition = ({ formData, updateFormData, setCurrentStep }) => {
  const navigate = useNavigate();
  const [localData, setLocalData] = useState({
    niche: formData.niche || '',
    uniqueValue: formData.uniqueValue || '',
    targetMarket: formData.targetMarket || ''
  });

  const handleInputChange = (field, value) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    updateFormData(localData);
    setCurrentStep(3);
    navigate('/goals');
  };

  const handlePrevious = () => {
    setCurrentStep(1);
    navigate('/basic-info');
  };

  const isFormValid = localData.niche && localData.uniqueValue && localData.targetMarket;

  const nicheExamples = [
    `${formData.subject} for ${formData.targetAudience}`,
    `Advanced ${formData.subject} techniques`,
    `${formData.subject} test preparation`,
    `${formData.subject} for beginners`,
    `${formData.subject} for professionals`,
    `Creative ${formData.subject} methods`
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <SafeIcon icon={FiTarget} className="text-2xl text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Define Your Niche</h2>
          <p className="text-gray-600">Identify what makes you unique in the education market</p>
        </div>

        {/* Educational Content */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiHelpCircle} className="text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">Why is defining your niche important?</h3>
              <p className="text-sm text-blue-700 mb-3">
                A clear niche helps you stand out in a crowded market, attract the right students, 
                and position yourself as an expert in a specific area.
              </p>
              <p className="text-sm text-blue-700">
                <strong>Examples:</strong> "SAT Math prep for high achievers", "Beginner piano for adults", 
                "Business English for professionals"
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Niche Definition */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Your Teaching Niche *
            </label>
            <textarea
              value={localData.niche}
              onChange={(e) => handleInputChange('niche', e.target.value)}
              placeholder="Describe your specific teaching niche in 1-2 sentences"
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            />
            
            {formData.subject && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Suggested niches based on your info:</p>
                <div className="flex flex-wrap gap-2">
                  {nicheExamples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => handleInputChange('niche', example)}
                      className="px-3 py-1 bg-gray-100 hover:bg-blue-100 text-sm rounded-full transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Unique Value Proposition */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What makes you unique? *
            </label>
            <textarea
              value={localData.uniqueValue}
              onChange={(e) => handleInputChange('uniqueValue', e.target.value)}
              placeholder="What special skills, experience, or approach do you bring? (e.g., 'I use gamification to make math fun', 'Former industry professional with real-world experience')"
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            />
          </div>

          {/* Target Market */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Describe your ideal student *
            </label>
            <textarea
              value={localData.targetMarket}
              onChange={(e) => handleInputChange('targetMarket', e.target.value)}
              placeholder="Who is your perfect student? Consider their age, goals, challenges, and motivation level"
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
          >
            Previous
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNext}
            disabled={!isFormValid}
            className={`px-8 py-3 rounded-lg font-semibold transition-all ${
              isFormValid
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Next: Set Your Goals
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default NicheDefinition;